import Swiper, { Autoplay, Navigation, Pagination } from 'swiper';
import { swiperObserver } from '../../utils/swiper.utils.js';

const registeredWrappers = new WeakSet();
const wrappersRegistry = new Set();
const equalizersRegistry = new WeakMap();
const rowTopTolerance = 4;
let resizeFrame = null;
let resizeListenerAdded = false;

const getTestimonialsSlides = (swiperEl) => {
	return Array.from(
		swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
	);
};

const getTestimonialsSlideRows = (swiperEl) => {
	const slides = getTestimonialsSlides(swiperEl);
	const swiper = swiperEl.swiper;

	if (!slides.length) {
		return [];
	}

	if (swiper && !swiper.destroyed) {
		return [slides];
	}

	const rows = [];

	slides.forEach((slide) => {
		const top = slide.getBoundingClientRect().top;
		const existingRow = rows.find((row) => {
			return Math.abs(row.top - top) <= rowTopTolerance;
		});

		if (existingRow) {
			existingRow.slides.push(slide);
			existingRow.top = Math.min(existingRow.top, top);
			return;
		}

		rows.push({
			top,
			slides: [slide],
		});
	});

	return rows
		.sort((a, b) => a.top - b.top)
		.map((row) => row.slides);
};

const getTestimonialsPositionGroups = (swiperEl) => {
	const rows = getTestimonialsSlideRows(swiperEl);
	const groups = [];

	rows.forEach((row) => {
		const positions = row
			.map((slide) => slide.querySelector('.testimonial-card__position'))
			.filter(Boolean);

		if (positions.length) {
			groups.push(positions);
		}
	});

	return groups;
};

const setupTestimonialsPositionEqualizer = (swiperEl) => {
	const equalizer = equalizersRegistry.get(swiperEl);

	if (equalizer) {
		equalizer.refresh();
		return;
	}

	if (typeof window.lineTitlesFunc !== 'function') {
		return;
	}

	equalizersRegistry.set(
		swiperEl,
		window.lineTitlesFunc([], {
			getGroups: () => getTestimonialsPositionGroups(swiperEl),
		})
	);
};

const setupTestimonialsWrapper = (wrapper) => {
	const swiperEl = wrapper.querySelector('.swiper');
	const slidesCount = swiperEl ? getTestimonialsSlides(swiperEl).length : 0;

	if (!swiperEl || !slidesCount) {
		return;
	}

	const paginationEl = wrapper.querySelector('.swiper-pagination');
	const nextEl = wrapper.querySelector('.swiper-button-next');
	const prevEl = wrapper.querySelector('.swiper-button-prev');
	const isDesktopStatic = window.innerWidth >= 1024 && slidesCount <= 3;

	if (isDesktopStatic) {
		if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
			swiperEl.swiper.destroy(true, true);
		}

		swiperEl.classList.add('no-swiper-type');
		wrapper.classList.add('is-static');
		setupTestimonialsPositionEqualizer(swiperEl);
		return;
	}

	swiperEl.classList.remove('no-swiper-type');
	wrapper.classList.remove('is-static');

	if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
		swiperEl.swiper.update();
		setupTestimonialsPositionEqualizer(swiperEl);
		return;
	}

	const swiper = new Swiper(swiperEl, {
		modules: [Autoplay, Navigation, Pagination],
		observer: true,
		observeParents: true,
		spaceBetween: 18,
		slidesPerView: 1.02,
		threshold: 10,
		watchOverflow: true,
		pagination: paginationEl
			? {
					el: paginationEl,
					clickable: true,
				}
			: undefined,
		navigation: nextEl && prevEl ? {nextEl, prevEl} : undefined,
		breakpoints: {
			390: {
				spaceBetween: 20,
				slidesPerView: 1.06,
			},
			641: {
				spaceBetween: 24,
				slidesPerView: 2.05,
			},
			821: {
				spaceBetween: 28,
				slidesPerView: 2.18,
			},
		},
		autoplay:
			slidesCount > 1
				? {
						delay: 4000,
						disableOnInteraction: false,
						pauseOnMouseEnter: window.innerWidth >= 1033,
					}
				: false,
	});

	if (typeof IntersectionObserver !== 'undefined') {
		swiperObserver(swiper);
	}

	setupTestimonialsPositionEqualizer(swiperEl);
};

const refreshTestimonialsWrappers = () => {
	wrappersRegistry.forEach((wrapper) => {
		setupTestimonialsWrapper(wrapper);
	});
};

const handleResize = () => {
	if (resizeFrame) {
		window.cancelAnimationFrame(resizeFrame);
	}

	resizeFrame = window.requestAnimationFrame(() => {
		resizeFrame = null;
		refreshTestimonialsWrappers();
	});
};

const addResizeListener = () => {
	if (resizeListenerAdded) {
		return;
	}

	resizeListenerAdded = true;
	window.addEventListener('resize', handleResize, { passive: true });
};

const initTestimonialsSwiper = (scope = document) => {
	const wrappers = scope.querySelectorAll('.testimonials-swiper-wr');

	wrappers.forEach((wrapper) => {
		if (!registeredWrappers.has(wrapper)) {
			registeredWrappers.add(wrapper);
			wrappersRegistry.add(wrapper);
		}

		setupTestimonialsWrapper(wrapper);
	});

	if (wrappers.length) {
		addResizeListener();
	}
};

initTestimonialsSwiper();
window.initTestimonialsSwiper = initTestimonialsSwiper;
